import { slugify as toSlug } from './LinkParser.js'
const WIKI_PIPE_TOKEN = '__KB_PIPE__'
const IMG_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i

export function markdownItWikilinks(md, options = {}) {
  const defaultRender =
    md.renderer.rules.link_open ||
    function (tokens, idx, opts, env, self) {
      return self.renderToken(tokens, idx, opts)
    }

  function tokenize(state, silent) {
    const max = state.posMax
    const start = state.pos
    const src = state.src
    // Avoid matching the embed syntax ![[...]] here; embed rule should handle it
    if (start > 0 && src.charCodeAt(start - 1) === 0x21 /* ! */) return false
    if (start + 4 >= max) return false
    if (src.charCodeAt(start) !== 0x5b /* [ */ || src.charCodeAt(start + 1) !== 0x5b /* [ */)
      return false

    let pos = start + 2
    while (pos < max) {
      if (src.charCodeAt(pos) === 0x5d /* ] */ && src.charCodeAt(pos + 1) === 0x5d /* ] */) break
      pos++
    }
    if (pos >= max) return false

    const rawInner = src.slice(start + 2, pos).trim()
    const inner = rawInner.split(WIKI_PIPE_TOKEN).join('|')
    if (!inner) return false

    if (!silent) {
      // Support multiple pipes; for typed entities allow [[type:id|Entity Label|Display]]
      const parts = inner.split('|')
      let target = parts[0]
      let label = parts.length >= 2 ? parts[1].trim() : ''
      // For typed entities with 3 parts, last part is display text
      const hasThird = parts.length >= 3
      const third = hasThird ? parts[2].trim() : ''
      target = String(target || '').trim()
      const hashIdx = target.indexOf('#')
      let heading = ''
      if (hashIdx >= 0) {
        heading = target.slice(hashIdx + 1).trim()
        target = target.slice(0, hashIdx).trim()
      }
      const isExternalUrl = /^https?:\/\//i.test(target)
      // Detect typed entity links like type:id and build a stable slug as type-id
      const typedMatch = !isExternalUrl && target.match(/^([a-zA-Z0-9_.-]+):(.+)$/)
      let entityType = ''
      let entityId = ''
      if (typedMatch) {
        entityType = typedMatch[1]
        entityId = typedMatch[2]
      }

      // Compute slug, but avoid fallback 'note' when target is empty (anchor-only)
      let slug = ''
      if (typedMatch) {
        slug = toSlug(`${entityType}-${entityId}`)
      } else if (target) {
        slug = toSlug(target)
      } else {
        slug = ''
      }
      // For typed entity links, use second segment as display when present (ignore modes)
      const isMode = (s) => {
        const v = String(s || '').toLowerCase()
        return v === 'inline' || v === 'compact' || v === 'card' || v === 'avatar'
      }
      let display = label || target || ''
      // For anchor-only links like [[#Heading]] with no explicit label,
      // use the heading text as the display so it doesn't render empty.
      if (!display && heading) display = heading
      if (typedMatch) {
        // Prefer the second segment unless it's a mode keyword
        if (!label || isMode(label)) {
          // If third segment is a non-mode, use it; otherwise fallback to target
          display = hasThird && !isMode(third) ? third : target || ''
        } else {
          display = label
        }
      }

      // If target looks like an image filename (no protocol), render as asset link
      const isAsset = IMG_EXT_RE.test(target) && !/^https?:/i.test(target)
      const tokenOpen = state.push('wikilink_open', 'a', 1)
      // Default href carries a fragment to preserve intent through sanitizers
      // - Anchor-only: '#h-<slug(heading)>'
      // - Note links:  '#n-<slug(target)>'
      let href = '#'
      if (!target && heading) {
        href = `#h-${toSlug(heading)}`
      } else if (!isExternalUrl && !isAsset && slug) {
        href = `#n-${slug}`
      }
      tokenOpen.attrs = [['href', href]]
      if (isAsset) {
        tokenOpen.attrs.push(['class', 'kb-assetlink'])
        tokenOpen.attrs.push(['data-asset', target])
      } else if (isExternalUrl) {
        tokenOpen.attrs.push(['class', 'kb-external-link'])
        tokenOpen.attrs[0][1] = target
        tokenOpen.attrs.push(['target', '_blank'])
        tokenOpen.attrs.push(['rel', 'noopener'])
      } else {
        tokenOpen.attrs.push(['class', 'kb-wikilink'])
        // For anchor-only links ([[#Heading]]), omit data-target and mark anchor-only
        if (heading && !target) {
          tokenOpen.attrs.push(['data-anchor-only', '1'])
          tokenOpen.attrs.push(['data-target', ''])
        } else {
          tokenOpen.attrs.push(['data-target', slug])
        }
        tokenOpen.attrs.push(['data-label', display])
        tokenOpen.attrs.push(['data-title', target])
        // Provide a readable tooltip and aid debugging/inspection
        if (target || display) tokenOpen.attrs.push(['title', target || display])
        if (typedMatch) {
          tokenOpen.attrs.push(['data-entity-type', entityType])
          tokenOpen.attrs.push(['data-entity-id', entityId])
          // Preserve entity canonical label (second segment) if provided
          if (label) tokenOpen.attrs.push(['data-entity-label', label])
        }
        if (heading) tokenOpen.attrs.push(['data-heading', heading])
      }

      const textToken = state.push('text', '', 0)
      textToken.content = display

      const tokenClose = state.push('wikilink_close', 'a', -1)
      if (process.env && process.env.NODE_ENV !== 'production') {
        try {
          console.debug('[Wikilinks] match', { target, heading, isAsset, display })
        } catch {}
      }
    }

    state.pos = pos + 2
    return true
  }

  md.inline.ruler.before('link', 'wikilink', tokenize)

  md.renderer.rules.wikilink_open = function (tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options)
  }
  md.renderer.rules.wikilink_close = function (tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options)
  }
}

export default markdownItWikilinks
